import {
  BundleJson,
  BundleStatus,
  CourierJson,
  Instruction,
} from '../../../types/bundleTypes';
import { OrderMonitor, Region } from '../../../types/orderTypes';
import { OrderInstruction } from '../../../components/maps/BundleMap';
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction } from 'react';
import dayjs from 'dayjs';

export interface BundleMonitorListProps {
  statusList: BundleStatus[];
  completedStatusList?: boolean;
  autoSort?: boolean;
  region?: Region;
}

export interface BundlesMonitorCollapseProps {
  region?: Region;
  isBundleLoading: boolean;
  bundleDeliveryStatus: BundleDeliveryStatus;
  isBundleOpen: boolean;
  bundle: BundleJson;
  bundleId: string;
  currentBundle: BundleJson;
  currentOrders: OrderInstruction[];
  updateSelectedBundleOrBundlesCallback: (bundleIds: string[]) => Promise<void>;
  dispatch: Dispatch<MonitorStorageActions>;
  expandedCallback: (bundleId: string, type?: 'collapse' | 'table') => void;
  toggleMapModal: Dispatch<SetStateAction<ModalState>>;
  toggleETAModal: Dispatch<SetStateAction<ModalState>>;
  toggleShelfModal: Dispatch<SetStateAction<ModalState>>;
  toggleCourierModal: Dispatch<SetStateAction<ModalState>>;
  toggleMoveOrdersModal: Dispatch<SetStateAction<MoveOrdersModal>>;
  togglePickupPointModal: Dispatch<SetStateAction<ModalState>>;
  lastViewMode: MonitorLiveViewModes;
  lastSelectedOrders: string[];
}

export interface BundlesMonitorOrdersTableWrapperProps {
  bundle: BundleJson;
  bundleId: string;
  modeActive: MonitorLiveViewModes;
  setBundleViewMode: Dispatch<SetStateAction<MonitorLiveViewModes>>;
  toggleETAModal: Dispatch<SetStateAction<ModalState>>;
  toggleShelfModal: Dispatch<SetStateAction<ModalState>>;
  togglePickupPointModal: Dispatch<SetStateAction<ModalState>>;
  dispatch: Dispatch<MonitorStorageActions>;
  region: Region;
  currentOrdersInstructions: OrderInstruction[];
  updateSelectedBundleOrBundlesCallback: (bundleIds: string[]) => Promise<void>;
  toggleMoveOrdersModal: Dispatch<SetStateAction<MoveOrdersModal>>;
  lastSelectedOrders: string[];
  isBundleLoading: boolean;
  isBundleOpen: boolean;
}

export interface OrdersTableProps {
  bundle: BundleJson;
  currentOrdersInstructions: OrderInstruction[];
  onSelectOrderInstruction: (
    orderInstructions: Array<OrderInstruction>
  ) => void;
  onSaveDndDraft?: (
    bundle: BundleJson,
    newOrderInstructions: Array<OrderInstruction>
  ) => void;
}

export interface TableBodyWrapperProps {
  pickupsGroup: InstructionGroup;
  expandedCallback: (virtualizationIndex: number) => void;
  virtualizationIndex: number;
  pickupsExpanded: boolean;
  reorderingView: boolean;
  currentOrdersInstructions: OrderInstruction[];
  currentSelectedRows: string[];
  handleChangeCheckbox: (event: ChangeEvent<HTMLInputElement>) => void;
  onRowMove: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
  windowColors: Map<string, string>;
  onRowClick: (event: MouseEvent<HTMLTableRowElement>) => void;
  onSearchOrderInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  searchOrderInputValue: string;
  rowsToHighlight: string[];
}

export interface TableRowWrapperProps {
  index: number;
  reorderingView: boolean;
  currentSelectedRows: string[];
  handleChangeCheckbox: (event: ChangeEvent<HTMLInputElement>) => void;
  onRowMove: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
  windowColors: Map<string, string>;
  instruction: Instruction;
  order: OrderMonitor;
  orderInstruction: OrderInstruction;
  label: string;
  collapsedPickupChecker: boolean;
  bgDiffTime: string;
  colorDiffTime: string;
  instructionTime: dayjs.Dayjs | null;
  newEta: string;
  eta: string;
  instructionTimeString: string;
  onRowClick: (event: MouseEvent<HTMLTableRowElement>) => void;
  bundleDeliveryDay: dayjs.Dayjs;
  rowsToHighlight: string[];
}

export interface ModalState {
  isOpen: boolean;
  bundleId?: string;
}

export interface MoveOrdersModal extends ModalState {
  selectedOrdersToMove?: string[];
}

export interface OrderInstructionsByBundles {
  [bundleId: string]: OrderInstruction[];
}

export interface MonitorBundlesWithOrders {
  bundles: BundleJson[];
  orders: { [bundleId: string]: OrderMonitor[] };
}

export type MonitorLiveViewModes = 'normal' | 'split' | 'move' | 'reorder';

export interface MonitorReducerStateType {
  bundles: BundleJson[];
  orders: OrderInstructionsByBundles;
  couriers: CourierJson[];
  bundlesInfoStatus: BundlesInfoStatus;
  loading: boolean;
  sortKey: string;
  sortType: string;
  autoSort: boolean;
}

export interface BundlesInfoStatus {
  [bundleId: string]: InfoStatus;
}

export interface InfoStatus {
  loading: boolean;
  bundleDeliveryStatus: BundleDeliveryStatus;
  collapseOpen: boolean;
  tablePickupsOpen: boolean;
  view: MonitorLiveViewModes;
  draftOrderList: OrderInstruction[];
  selectedOrders: string[];
}

export type BundleDeliveryStatus =
  | 'on-time'
  | 'delayed'
  | 'finished'
  | 'not-started';

export interface InstructionGroup {
  index: number;
  instruction: Instruction;
  address: string;
  count: number;
}

export type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export enum Types {
  AddNewBundle = 'ADD_NEW_BUDNLE',
  DeleteBundle = 'DELETE_BUNDLE',
  SetLoading = 'SET_LOADING',
  StoreAllFetchedData = 'STORE_ALL_FETCHED_DATA',
  UpdateSelectedBundle = 'UPDATED_SELECTED_BUNDLE',
  UpdateSelectedBundles = 'UPDATED_SELECTED_BUNDLES',
  SetLoadingForBundle = 'SET_LOADING_FOR_BUNDLE',
  SetLoadingForBundles = 'SET_LOADING_FOR_BUNDLES',
  UpdateBundlesInfoStatus = 'UPDATE_BUNDLES_INFO_STATUS',
  UpdateBundlesInfoStatusAndSortBundles = 'UPDATE_BUNDLES_INFO_STATUS_AND_SORT_BUNDLES',
  SetSortByKey = 'SET_SORT_BY_KEY',
  SetAutoSort = 'SET_AUTO_SORT',
  ToggleCollapseOrTableView = 'TOGGLE_COLLAPSE_OR_TABLE_VIEW',
  CollapseAll = 'COLLAPSE_ALL',
  StoreLastChangesInBundle = 'STORE_LAST_CHANGES_IN_BUNDLE',
}

export type MonitorReducerStoragePayload = {
  [Types.AddNewBundle]: {
    bundles: BundleJson[];
    orders: OrderInstructionsByBundles;
  };
  [Types.DeleteBundle]: {
    bundleId: string;
  };
  [Types.SetLoading]: {
    loading: boolean;
  };
  [Types.StoreAllFetchedData]: {
    bundles: BundleJson[];
    orders: OrderInstructionsByBundles;
    couriers: CourierJson[];
    bundlesInfoStatus: BundlesInfoStatus;
    loading: boolean;
    autoSort: boolean;
  };
  [Types.UpdateSelectedBundle]: {
    bundleId: string;
    bundles: BundleJson[];
    order: OrderInstruction[];
  };
  [Types.UpdateSelectedBundles]: {
    bundleIds: string[];
    bundles: BundleJson[];
    orders: OrderInstructionsByBundles;
  };
  [Types.SetLoadingForBundle]: {
    bundlesLoadingStatus: boolean;
    bundleId: string;
  };
  [Types.SetLoadingForBundles]: {
    bundlesLoadingStatus: boolean;
    bundleIds: string[];
  };
  [Types.UpdateBundlesInfoStatus]: {
    bundlesInfoStatus: BundlesInfoStatus;
  };
  [Types.UpdateBundlesInfoStatusAndSortBundles]: {
    bundles: BundleJson[];
    bundlesInfoStatus: BundlesInfoStatus;
  };
  [Types.ToggleCollapseOrTableView]: {
    bundleId: string;
    type: 'collapse' | 'table';
  };
  [Types.CollapseAll]: {
    bundleIdsToClose: string[];
  };
  [Types.StoreLastChangesInBundle]: {
    bundleId: string;
    view?: MonitorLiveViewModes;
    draftOrderList?: OrderInstruction[];
    selectedOrders?: string[];
  };
  [Types.SetSortByKey]: {
    sortKey: string;
  };
  [Types.SetAutoSort]: {
    autoSort: boolean;
  };
};

export type MonitorStorageActions =
  ActionMap<MonitorReducerStoragePayload>[keyof ActionMap<MonitorReducerStoragePayload>];
